import React, { useMemo, useState } from 'react';
import { AlertTriangleIcon, ArrowRightIcon, CalendarDaysIcon, CheckCircle2Icon, ChevronRightIcon, ClockIcon, FileTextIcon, PlayIcon, ReceiptIcon, WrenchIcon } from 'lucide-react';
import { CompanySettings, Employee, ProInvoice, ProJob, Quote, TimeEntry } from '../../types/pro';
import { JOB_STATUS, formatTime, isSameDay, kr } from '../../utils/pro';
import { ActionItem, actionItems, moneyPicture, weekHours } from '../../utils/dashboard';
import { PageHeader } from '../../components/pro/PageHeader';
import { StatusChip } from '../../components/pro/StatusChip';
import { EmptyState } from '../../components/pro/EmptyState';
interface ProDashboardProps {
  quotes: Quote[];
  jobs: ProJob[];
  invoices: ProInvoice[];
  employees: Employee[];
  timeEntries: TimeEntry[];
  company: CompanySettings;
  onOpenJob: (jobId: string) => void;
  onOpenInvoice: (invoiceId: string) => void;
  onOpenQuote: (quoteId: string) => void;
  onNavigate: (target: 'jobs' | 'invoices' | 'calendar' | 'time') => void;
  onStartJob: (job: ProJob) => void;
}
const WEEKDAYS = ['M', 'T', 'O', 'T', 'F', 'L', 'S'];
const ACTION_ICON = {
  'faktura-mangler': ReceiptIcon,
  forfalden: AlertTriangleIcon,
  'tilbud-uden-svar': FileTextIcon,
  'igang-uden-timer': ClockIcon,
  'ikke-planlagt': CalendarDaysIcon
} as const;
export function ProDashboard({
  quotes,
  jobs,
  invoices,
  employees,
  timeEntries,
  company,
  onOpenJob,
  onOpenInvoice,
  onOpenQuote,
  onNavigate,
  onStartJob
}: ProDashboardProps) {
  const [showAllActions, setShowAllActions] = useState(false);
  const employeeById = useMemo(() => new Map(employees.map(item => [item.id ?? '', item])), [employees]);
  const money = useMemo(() => moneyPicture(jobs, invoices, timeEntries, company), [jobs, invoices, timeEntries, company]);
  const week = useMemo(() => weekHours(timeEntries), [timeEntries]);
  const actions = useMemo(() => actionItems(jobs, invoices, quotes, timeEntries, company), [jobs, invoices, quotes, timeEntries, company]);
  const today = useMemo(() => {
    const now = new Date();
    return jobs.filter(job => job.start && isSameDay(new Date(job.start), now)).sort((a, b) => (a.start ?? '').localeCompare(b.start ?? ''));
  }, [jobs]);
  const upcoming = useMemo(() => {
    const now = new Date();
    return jobs.filter(job => job.start && new Date(job.start) > now).sort((a, b) => (a.start ?? '').localeCompare(b.start ?? '')).slice(0, 5);
  }, [jobs]);
  const running = jobs.filter(job => job.status === 'i-gang');
  const visibleActions = showAllActions ? actions : actions.slice(0, 5);
  const urgentCount = actions.filter(item => item.urgent).length;
  const maxDay = Math.max(...week.perDay, 8);
  const todayIndex = (new Date().getDay() + 6) % 7;
  const openAction = (item: ActionItem) => {
    if (item.target.kind === 'job') onOpenJob(item.target.id);
    if (item.target.kind === 'invoice') onOpenInvoice(item.target.id);
    if (item.target.kind === 'quote') onOpenQuote(item.target.id);
  };
  const card = 'rounded-xl border border-stone-200 bg-white';
  return <div className="space-y-5" data-id="element-1176" data-mp="3b3zq91doo5vu">
      <PageHeader title="Overblik" subtitle={new Date().toLocaleDateString('da-DK', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    })} actions={running.length > 0 && <button onClick={() => running[0].id && onOpenJob(running[0].id)} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700" data-id="element-1178" data-mp="3218cuw18z01k">
              <span className="h-2 w-2 rounded-full bg-white" data-id="element-1179" data-mp="2n8533atwdqle" />
              {running.length === 1 ? `I gang: ${running[0].jobNo}` : `${running.length} opgaver i gang`}
            </button>} data-id="element-1177" data-mp="3b37v1ro00a63" />

      {/* Money — "what have I earned that nobody has paid me for yet" is the
          question a one-man company opens the app to answer. */}
      <section aria-label="Penge" className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-id="element-1180" data-mp="ye7z7kvvna7p">
        <button onClick={() => onNavigate('jobs')} className="rounded-xl bg-stone-900 p-5 text-left transition-colors hover:bg-stone-800 lg:p-6" data-id="element-1181" data-mp="jgnvehapvl34">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-1182" data-mp="aovqem4gp45x">
            Klar til fakturering
          </p>
          <p className="mt-2 text-3xl font-bold tabular-nums text-white lg:text-4xl" data-id="element-1183" data-mp="fh6pxnvainxy">
            {kr(money.unbilled)}
          </p>
          <p className="mt-1.5 text-sm text-stone-400" data-id="element-1184" data-mp="5q2cwtp6dd5r">
            {money.unbilledJobCount === 0 ? 'Alt arbejde er faktureret' : `${money.unbilledJobCount} ${money.unbilledJobCount === 1 ? 'opgave' : 'opgaver'} med timer eller materialer`}
          </p>
        </button>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-2" data-id="element-1185" data-mp="39a03gghhqb7w">
          {[{
          label: 'Sendt, venter betaling',
          value: kr(money.outstanding),
          tone: 'text-stone-900',
          foot: 'Fakturaer hos kunden',
          target: 'invoices' as const
        }, {
          label: 'Forfaldne',
          value: kr(money.overdue),
          tone: money.overdue > 0 ? 'text-red-600' : 'text-stone-900',
          foot: money.overdueCount === 0 ? 'Ingen betaler for sent' : `${money.overdueCount} ${money.overdueCount === 1 ? 'faktura' : 'fakturaer'} over forfald`,
          target: 'invoices' as const
        }, {
          label: 'Betalt denne måned',
          value: kr(money.paidThisMonth),
          tone: 'text-emerald-700',
          foot: 'Penge på kontoen',
          target: 'invoices' as const
        }, {
          label: 'Timer denne uge',
          value: `${week.total.toLocaleString('da-DK')} t`,
          tone: 'text-stone-900',
          foot: `${week.billable.toLocaleString('da-DK')} t kan faktureres`,
          target: 'time' as const
        }].map(item => <button key={item.label} onClick={() => onNavigate(item.target)} className={`${card} p-5 text-left transition-colors hover:bg-stone-50`} data-id="element-1186" data-mp="3mb9fzxh54mk0">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-1187" data-mp="3ve2a08hpjcdj">
                {item.label}
              </p>
              <p className={`mt-1.5 text-xl font-bold tabular-nums ${item.tone}`} data-id="element-1188" data-mp="44bxdji8fkpj">
                {item.value}
              </p>
              <p className="mt-1 text-xs text-stone-500" data-id="element-1189" data-mp="25yudcjvzutyb">{item.foot}</p>
            </button>)}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3" data-id="element-1190" data-mp="1ygmsnxxffg9g">
        {/* Today's work */}
        <section className={`${card} overflow-hidden lg:col-span-2`} data-id="element-1191" data-mp="1tlqndw36iwee">
          <header className="flex items-center gap-2 border-b border-stone-100 px-5 py-4" data-id="element-1192" data-mp="1opicwvzxfrdk">
            <h2 className="flex-1 text-base font-semibold text-stone-900" data-id="element-1193" data-mp="1jqoxgy12xqar">
              I dag
            </h2>
            <button onClick={() => onNavigate('calendar')} className="inline-flex items-center gap-1 text-sm font-medium text-stone-500 transition-colors hover:text-brand-700" data-id="element-1194" data-mp="2hc315nl3vncw">
              Kalender
              <ArrowRightIcon className="h-3.5 w-3.5" data-id="element-1195" data-mp="23dmz49y6ul13" />
            </button>
          </header>

          {today.length === 0 ? <EmptyState icon={CalendarDaysIcon} title="Ingen opgaver planlagt i dag" description="Læg en opgave i kalenderen, så står den her på dagen." data-id="element-1196" data-mp="w0bfy1g4f9uf" /> : <ul className="divide-y divide-stone-100" data-id="element-1197" data-mp="2r2g55mv47vhc">
              {today.map(job => {
            const employee = employeeById.get(job.employeeIds[0] ?? '');
            const canStart = ['planlagt', 'ikke-planlagt', 'afventer'].includes(job.status);
            return <li key={job.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5 transition-colors hover:bg-stone-50" data-id="element-1198" data-mp="2n3h78m7swyze">
                    <span className="w-12 flex-shrink-0 text-sm font-bold tabular-nums text-stone-900" data-id="element-1199" data-mp="3eumzyhl6zuen">
                      {formatTime(job.start ?? '')}
                    </span>
                    <button onClick={() => job.id && onOpenJob(job.id)} className="min-w-0 flex-1 text-left" data-id="element-1200" data-mp="35yntmzdk3j2f">
                      <span className="block truncate text-sm font-medium text-stone-900" data-id="element-1201" data-mp="2c6zeo0fm2ay">
                        {job.jobNo} · {job.title}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-stone-500" data-id="element-1202" data-mp="7p5bklki74q3">
                        {job.customerName} · {job.address}, {job.city}
                      </span>
                    </button>
                    {employee && employees.length > 1 && <span className="hidden items-center gap-1.5 text-xs font-medium text-stone-600 sm:flex" data-id="element-1203" data-mp="gja02yc6042g">
                        <span className="h-2 w-2 rounded-full" style={{
                  backgroundColor: employee.color
                }} data-id="element-1204" data-mp="3kvf4qiatzxsg" />
                        {employee.name.split(' ')[0]}
                      </span>}
                    <StatusChip meta={JOB_STATUS[job.status]} data-id="element-1205" data-mp="1e4phnlsxt705" />
                    {canStart && <button onClick={() => onStartJob(job)} className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-stone-900 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-stone-800" data-id="element-1206" data-mp="195l7arua1388">
                        <PlayIcon className="h-3 w-3" data-id="element-1207" data-mp="1dmgmub3hruge" />
                        Start
                      </button>}
                  </li>;
          })}
            </ul>}
        </section>

        {/* The week at a glance */}
        <section className={`${card} p-5`} data-id="element-1208" data-mp="14jp8o3j8ekl2">
          <h2 className="text-base font-semibold text-stone-900" data-id="element-1209" data-mp="zm5vl6y9sdup">Din uge</h2>
          <p className="mt-0.5 text-xs text-stone-500" data-id="element-1210" data-mp="unm9n6jof22c">
            {week.total.toLocaleString('da-DK')} timer registreret
          </p>

          <div className="mt-5 flex items-end justify-between gap-1.5" data-id="element-1211" data-mp="ppkomg2owso8">
            {week.perDay.map((hours, index) => <div key={index} className="flex flex-1 flex-col items-center gap-2" data-id="element-1212" data-mp="2p13ps8hr4wnc" data-mpi={index}>
                <span className="text-[10px] font-medium tabular-nums text-stone-400" data-id="element-1213" data-mp="2k3tzkqmjggyg">
                  {hours > 0 ? hours.toLocaleString('da-DK') : ''}
                </span>
                <div className={`w-full rounded-t transition-colors ${index === todayIndex ? 'bg-brand-500' : 'bg-stone-200'}`} style={{
              height: `${Math.max(3, hours / maxDay * 76)}px`
            }} role="presentation" data-id="element-1214" data-mp="2wkcxzyeiupat" />
                <span className={`text-[11px] font-semibold ${index === todayIndex ? 'text-brand-700' : 'text-stone-400'}`} data-id="element-1215" data-mp="29n1m8ypcpuqy">
                  {WEEKDAYS[index]}
                </span>
              </div>)}
          </div>

          <button onClick={() => onNavigate('time')} className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg border border-stone-200 py-2.5 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50" data-id="element-1216" data-mp="3u0incqwlbvdk">
            <ClockIcon className="h-4 w-4" data-id="element-1217" data-mp="399mw35q3ukm1" />
            Registrer timer
          </button>
        </section>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3" data-id="element-1218" data-mp="34er0xwpknh4p">
        {/* The derived to-do list — this is the part you act on */}
        <section className={`${card} overflow-hidden lg:col-span-2`} data-id="element-1219" data-mp="3ea17mxqx0cot">
          <header className="flex items-center gap-3 border-b border-stone-100 px-5 py-4" data-id="element-1220" data-mp="3e8py0hasgaps">
            <h2 className="flex-1 text-base font-semibold text-stone-900" data-id="element-1221" data-mp="2qinbegpn7wyk">
              Kræver handling
            </h2>
            {urgentCount > 0 && <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700" data-id="element-1222" data-mp="30bkzr3hbgg53">
                {urgentCount} haster
              </span>}
          </header>

          {actions.length === 0 ? <EmptyState icon={CheckCircle2Icon} title="Alt er under kontrol" description="Ingen ufakturerede opgaver, forfaldne fakturaer eller ubesvarede tilbud." data-id="element-1223" data-mp="27sdxwaoeoirk" /> : <>
              <ul className="divide-y divide-stone-100" data-id="element-1224" data-mp="n4d27iuqddfx">
                {visibleActions.map(item => {
              const Icon = ACTION_ICON[item.kind];
              return <li key={item.id} data-id="element-1225" data-mp="ddic23ed5tp7">
                      <button onClick={() => openAction(item)} className="group flex w-full items-center gap-3.5 px-5 py-3.5 text-left transition-colors hover:bg-stone-50" data-id="element-1226" data-mp="dejoab748m8i">
                        <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${item.urgent ? 'bg-red-50 text-red-600' : 'bg-stone-100 text-stone-500'}`} data-id="element-1227" data-mp="s3g26ovusth1">
                          <Icon className="h-4 w-4" data-id="element-1228" data-mp="3gwblc403zwdk" />
                        </span>
                        <span className="min-w-0 flex-1" data-id="element-1229" data-mp="xiw9mgkjty9">
                          <span className="block truncate text-sm font-medium text-stone-900" data-id="element-1230" data-mp="3sbompp9myjby">
                            {item.title}
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-stone-500" data-id="element-1231" data-mp="3na9403uxkowq">
                            {item.detail}
                          </span>
                        </span>
                        <span className={`flex-shrink-0 text-sm font-semibold tabular-nums ${item.urgent ? 'text-red-600' : 'text-stone-600'}`} data-id="element-1232" data-mp="jtiql3x0r9l0">
                          {item.meta}
                        </span>
                        <ChevronRightIcon className="h-4 w-4 flex-shrink-0 text-stone-300 transition-colors group-hover:text-brand-600" data-id="element-1233" data-mp="2unocn3eggct6" />
                      </button>
                    </li>;
            })}
              </ul>

              {actions.length > 5 && <button onClick={() => setShowAllActions(!showAllActions)} className="w-full border-t border-stone-100 py-3 text-sm font-semibold text-stone-500 transition-colors hover:bg-stone-50 hover:text-stone-900" data-id="element-1234" data-mp="1627f1fvp1p9x">
                  {showAllActions ? 'Vis færre' : `Vis alle ${actions.length} punkter`}
                </button>}
            </>}
        </section>

        {/* What's next */}
        <section className={`${card} overflow-hidden`} data-id="element-1235" data-mp="1xv6oxkhbqnbf">
          <header className="border-b border-stone-100 px-5 py-4" data-id="element-1236" data-mp="1xvpwc79cltb7">
            <h2 className="text-base font-semibold text-stone-900" data-id="element-1237" data-mp="22uje4bvo46h6">
              Næste aftaler
            </h2>
          </header>
          {upcoming.length === 0 ? <EmptyState icon={WrenchIcon} title="Ingen aftaler i kalenderen" data-id="element-1238" data-mp="8ga2dz3zwe5w" /> : <ul className="divide-y divide-stone-100" data-id="element-1239" data-mp="1bs7b85xzpyoc">
              {upcoming.map(job => {
            const date = new Date(job.start ?? '');
            return <li key={job.id} data-id="element-1240" data-mp="1ksdktgqkiei8">
                    <button onClick={() => job.id && onOpenJob(job.id)} className="group flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-stone-50" data-id="element-1241" data-mp="1ksng9judtdvb">
                      <span className="flex w-11 flex-shrink-0 flex-col items-center rounded-lg border border-stone-200 bg-stone-50 py-1.5" data-id="element-1242" data-mp="1pqyl1g5tky6o">
                        <span className="text-[10px] font-semibold uppercase text-stone-500" data-id="element-1243" data-mp="23hagnp1m5f2k">
                          {date.toLocaleDateString('da-DK', {
                      weekday: 'short'
                    })}
                        </span>
                        <span className="text-base font-bold leading-none text-stone-800" data-id="element-1244" data-mp="28hopvaeyav26">
                          {date.getDate()}
                        </span>
                      </span>
                      <span className="min-w-0 flex-1" data-id="element-1245" data-mp="2x2b8xmg4aguu">
                        <span className="block truncate text-sm font-medium text-stone-900" data-id="element-1246" data-mp="2x1t626yy94dy">
                          {job.title}
                        </span>
                        <span className="block truncate text-xs text-stone-500" data-id="element-1247" data-mp="2n3m16mhxqcsb">
                          {formatTime(job.start ?? '')} · {job.customerName}
                        </span>
                      </span>
                      <ChevronRightIcon className="h-4 w-4 flex-shrink-0 text-stone-300 transition-colors group-hover:text-brand-600" data-id="element-1248" data-mp="2e4ii3iirpxdt" />
                    </button>
                  </li>;
          })}
            </ul>}
        </section>
      </div>
    </div>;
}